// src/lib/cacheManager.js
// Handles cache versioning, clearing, and session recovery

import { logger } from './logger';

// App version - increment this when you want to force cache clear for all users
const APP_CACHE_VERSION = '1';
const CACHE_VERSION_KEY = 'kidconnect_cache_version';

/**
 * Check if cache version matches current app version
 * If not, clear all caches and update version
 */
export const checkCacheVersion = () => {
  const storedVersion = localStorage.getItem(CACHE_VERSION_KEY);

  if (storedVersion !== APP_CACHE_VERSION) {
    logger.debug('Cache version mismatch, clearing caches...');
    clearAllCaches();
    localStorage.setItem(CACHE_VERSION_KEY, APP_CACHE_VERSION);
    return true; // Cache was cleared
  }

  return false; // Cache is current
};

/**
 * Clear all application caches including:
 * - Supabase auth tokens (sb-* keys)
 * - React Query cache (handled separately via queryClient)
 * - App-specific localStorage items
 * - SessionStorage
 */
export const clearAllCaches = () => {
  logger.debug('Clearing all caches...');

  // Get all localStorage keys
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    // Keep only the cache version key itself
    if (key && key !== CACHE_VERSION_KEY) {
      keysToRemove.push(key);
    }
  }

  // Remove all keys except version
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
  });

  // Clear sessionStorage
  sessionStorage.clear();

  logger.debug('All caches cleared');
};

/**
 * Clear only Supabase auth-related caches
 * Use this for login/logout issues
 */
export const clearAuthCaches = () => {
  logger.debug('Clearing auth caches...');

  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    // Supabase stores auth data in keys starting with 'sb-'
    if (key && key.startsWith('sb-')) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
  });

  logger.debug('Auth caches cleared');
};

/**
 * Clear React Query cache
 * Call this with the queryClient instance
 */
export const clearQueryCache = (queryClient) => {
  if (queryClient) {
    logger.debug('Clearing React Query cache...');
    queryClient.clear();
    logger.debug('React Query cache cleared');
  }
};

/**
 * Full reset - clear everything and reload
 * Use as emergency recovery option
 */
export const hardReset = () => {
  logger.debug('Performing hard reset...');
  clearAllCaches();
  // Force reload from server (bypass browser cache)
  // #region agent log
  // Ingest disabled: 127.0.0.1 is blocked by CSP in prod
  // #endregion
  window.location.reload(true);
};

/**
 * Soft reset - clear caches but keep user on current page
 * Returns a promise that resolves when ready to continue
 */
export const softReset = async (queryClient, signOutFn) => {
  logger.debug('Performing soft reset...');

  // Clear React Query cache
  if (queryClient) {
    queryClient.clear();
  }

  // Sign out from Supabase (this clears auth state properly)
  if (signOutFn) {
    try {
      await signOutFn();
    } catch (e) {
      logger.error('Error during signOut:', e);
    }
  }

  // Clear remaining localStorage (except version)
  clearAllCaches();

  logger.debug('Soft reset complete');
};

/**
 * Check if there are signs of corrupted session data
 * Call this on app load to detect issues early
 */
export const detectCorruptedSession = () => {
  const issues = [];

  // Check for Supabase auth keys without valid structure
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('sb-')) {
      try {
        const value = localStorage.getItem(key);
        if (value) {
          JSON.parse(value); // Will throw if corrupted
        }
      } catch (e) {
        issues.push('Corrupted auth data detected');
      }
    }
  }

  if (issues.length > 0) {
    logger.warn('Detected corrupted session data');
    return issues;
  }

  return null;
};

/**
 * Export version for display in UI
 */
export const getCacheVersion = () => APP_CACHE_VERSION;
